// `ScreenplayTypst` (SuiteCompile/Screenplay.swift): Typst source for a screenplay PDF with every line placed on the
// page grid, so the layout is exactly the one `scriptLayout` worked out (and its page count is the script's).
import { fountainRuns } from '@writing-suite/core';
import { bylineName, pageInches } from '../manuscript.ts';
import { escape, escapeString } from '../typst/writer.ts';
import type { CompiledManuscript } from '../types.ts';
import { printedStageScript } from './stage.ts';
import { stagePageCountOf, stageTypst, stageUKSetup, stageUKSource } from './stageTypst.ts';
import { count, LEFT_MARGIN, length, run, screenplayScript, scriptLayout, TOP_MARGIN, WIDTH, type Run, type TitleField } from './script.ts';
import { multicamPageCountOf, multicamTypst } from './multicam.ts';
import { RADIO_FALLBACKS, radioEstimatedPages, radioTypst } from './radio.ts';

/** Points: Courier 12 is ten to the inch. */
const COLUMN = 7.2;
/** Points: six lines to the inch. */
const LINE = 12.0;

/** `String(format: "%.2f", d)`. */
const num = (d: number) => d.toFixed(2);

/** The font list the script's source names (for loading fonts). */
export function screenplayFontFamilies(m: CompiledManuscript): string[] {
  if (m.preset.render.screenplay?.format === 'stage-uk') return [m.preset.render.font.family, 'Times New Roman', 'Libertinus Serif'].filter((f, i, all) => all.indexOf(f) === i);
  if (m.preset.render.screenplay?.format === 'radio') return [m.preset.render.font.family, ...RADIO_FALLBACKS].filter((f, i, all) => all.indexOf(f) === i);
  const fonts = [m.preset.render.font.family, 'Courier Prime', 'Courier', 'Courier New', 'DejaVu Sans Mono'];
  return fonts.filter((f, i) => !fonts.slice(0, i).includes(f));
}

export interface ScreenplayTypstOptions {
  /** Bold drawn as a stroke, for a system Courier whose bold Typst can't pick out. The Mac decides by what's installed;
   *  the web ships Courier Prime, so never. */
  fauxBold?: boolean;
}

/** `ScreenplayTypst.source`. A US play is set as a play (`stageTypst`); a UK one needs Typst's flow and, for a sample,
 *  Typst to count its pages: `stageUKTypst`. */
export function screenplayTypst(m: CompiledManuscript, o: ScreenplayTypstOptions = {}): string {
  const settings = m.preset.render.screenplay;
  if (settings?.format === 'stage-uk') return stageUKSource(printedStageScript(m), stageUKSetup(m));
  if (settings?.format === 'stage-us') return stageTypst(m, o);
  if (settings?.format === 'multicam') return multicamTypst(m, o);
  if (settings?.format === 'radio') return radioTypst(m);
  const script = screenplayScript(m);
  const size = pageInches(m.preset.render.page.size);
  const layout = scriptLayout(script, size.height, settings?.sceneHeadingSpacing ?? 1);
  const left = LEFT_MARGIN * 72, top = TOP_MARGIN * 72;
  let out = screenplayPreamble(m, screenplayFontFamilies(m));
  const faux = o.fauxBold ?? false;
  const place = (x: number, y: number, runs: Run[], bold = false) =>
    `#place(top + left, dx: ${num(x)}pt, dy: ${num(y)}pt)[${markup(runs, bold, faux)}]\n`;
  const plain = (s: string) => [run(s)];
  if (m.preset.render.titlePage.include) out += screenplayTitlePage(m, script.titlePage, layout.linesPerPage, faux);

  // The script. Page one has no number; the rest are numbered "2." at the top right.
  const bold = settings?.boldSceneHeadings ?? false;
  layout.pages.forEach((scriptPage, n) => {
    let page = '';
    if (n > 0) {
      const number = `${n + 1}.`;
      page += place(left + (WIDTH - count(number)) * COLUMN, top / 2, plain(number));
    }
    for (const l of scriptPage.lines) {
      const y = top + l.row * LINE;
      page += place(left + l.column * COLUMN, y, l.runs, bold && l.isSceneHeading);
      if (l.sceneNumber !== undefined) {
        page += place(left - (count(l.sceneNumber) + 3) * COLUMN, y, plain(l.sceneNumber));
        page += place(left + (WIDTH + 2) * COLUMN, y, plain(l.sceneNumber));
      }
    }
    out += `#page[\n${page}]\n`;
  });
  return out;
}

/** `ScreenplayTypst.preamble`: the document, page and text settings every script's source starts with — Courier on a page
 *  with no margins of its own (every line is placed). */
export function screenplayPreamble(m: CompiledManuscript, fonts: readonly string[]): string {
  const size = pageInches(m.preset.render.page.size);
  return `#set document(title: "${escapeString(m.title)}"${m.author ? `, author: "${escapeString(bylineName(m.author))}"` : ''})
#set page(width: ${num(size.width * 72)}pt, height: ${num(size.height * 72)}pt, margin: 0pt)
#set text(font: (${fonts.map((f) => `"${escapeString(f)}"`).join(', ')}), size: ${num(m.preset.render.font.size)}pt, lang: "${[...m.language].slice(0, 2).join('')}", hyphenate: false, ligatures: false)
#set smartquote(enabled: false)
`;
}

/** `ScreenplayTypst.titlePage`: a screenplay's (or sitcom's) title page. Values keep their Fountain emphasis
 *  (`_**BRICK & STEEL**_`), set as styles. */
export function screenplayTitlePage(m: CompiledManuscript, fields: readonly TitleField[], linesPerPage: number, faux = false): string {
  const width = pageInches(m.preset.render.page.size).width * 72;
  const left = LEFT_MARGIN * 72, top = TOP_MARGIN * 72;
  const place = (x: number, y: number, runs: Run[]) => `#place(top + left, dx: ${num(x)}pt, dy: ${num(y)}pt)[${markup(runs, false, faux)}]\n`;
  const centred = (runs: Run[], row: number) => place((width - length(runs) * COLUMN) / 2, top + row * LINE, runs);
  {
    let page = '';
    const field = (key: string) => fields.find((f) => f.key === key)?.value;
    let row = 20;
    for (const l of titleLines(field('title') ?? m.title, true)) { page += centred(l, row); row += 1; }
    row += 3;
    const credit = field('credit');
    if (credit !== undefined) { for (const l of titleLines(credit)) { page += centred(l, row); row += 1; } row += 1; }
    const author = field('author') ?? field('authors');
    if (author !== undefined) for (const l of titleLines(author)) { page += centred(l, row); row += 1; }
    const source = field('source');
    if (source !== undefined) { row += 2; for (const l of titleLines(source)) { page += centred(l, row); row += 1; } }
    const bottom = linesPerPage - 1;
    const contact = titleLines(field('contact') ?? '');
    contact.forEach((l, i) => { page += place(left, top + (bottom - contact.length + 1 + i) * LINE, l); });
    const corner = [field('draft date'), field('date'), field('copyright'), field('notes')]
      .filter((v): v is string => v !== undefined).flatMap((v) => titleLines(v));
    corner.forEach((l, i) => { page += place(left + (WIDTH - length(l)) * COLUMN, top + (bottom - corner.length + 1 + i) * LINE, l); });
    return `#page[\n${page}]\n`;
  }
}

/** `ScreenplayTypst.titleLines`: a title-page value's lines (empty lines dropped) as styled runs, markers removed. */
export function titleLines(value: string, uppercased = false): Run[][] {
  return value.split('\n').filter((l) => l !== '').map((line) =>
    fountainRuns(line, 0, line.length).flatMap((r) => {
      const text = uppercased ? r.text.toUpperCase() : r.text;
      return text === '' ? [] : [{ ...r, text }];
    }));
}

/** `ScreenplayTypst.markup`. Non-breaking spaces keep the columns (Typst would collapse runs of spaces); without Courier
 *  Prime, bold is drawn a hair heavier instead (the old typewriter way: struck twice). */
export function markup(runs: Run[], bold: boolean, faux = false): string {
  return runs.map((r) => {
    let s = escape(r.text).replaceAll(' ', '\u00A0');
    if (r.italic) s = `#emph[${s}]`;
    if (r.bold || bold) s = faux ? `#text(stroke: 0.45pt + black)[${s}]` : `#strong[${s}]`;
    if (r.underline) s = `#underline[${s}]`;
    return s;
  }).join('');
}

/** `ScreenplayTypst.pageCount`: how many pages the script runs (not counting the title page): its length in minutes. */
export function screenplayPageCount(m: CompiledManuscript): number {
  if (m.preset.render.screenplay?.format === 'stage-us' || m.preset.render.screenplay?.format === 'stage-uk') return stagePageCountOf(m);
  if (m.preset.render.screenplay?.format === 'multicam') return multicamPageCountOf(m);
  if (m.preset.render.screenplay?.format === 'radio') return radioEstimatedPages(m);
  return scriptLayout(screenplayScript(m), pageInches(m.preset.render.page.size).height, m.preset.render.screenplay?.sceneHeadingSpacing ?? 1).pages.length;
}
